import styled from "@emotion/styled";
import type { MutableRefObject, SyntheticEvent } from "react";
import type { FC } from "react";

import { MDSText, MDSTextColor, MDSTextVariant } from "@/mds";
import type { LocalAudioRecordingBlobRecord } from "@/domains/dexie-db/models/audio-recording";
import { AudioRecordingBlobList } from "@/pages/audio-recordings/shared";

export type CapturedBlobsStats = {
  totalDurationLabel: string;
  totalSizeLabel: string;
  uploadedCount: number;
  blobCount: number;
};

type PlaybackProps = {
  audioElementRef: MutableRefObject<HTMLAudioElement | null>;
  playbackUrl: string | null;
  onAudioEnd: () => void;
  onAudioError: (event: SyntheticEvent<HTMLAudioElement>) => void;
};

type Props = {
  blobs: LocalAudioRecordingBlobRecord[];
  stats?: CapturedBlobsStats | null;
  emptyMessage?: string;
  title?: string;
  playback?: PlaybackProps;
};

export const CapturedBlobsSectionContent: FC<Props> = ({
  blobs,
  stats = null,
  emptyMessage = "No blobs recorded yet.",
  title = "Captured Blobs",
  playback,
}) => (
  <>
    <SectionHeader>
      <MDSText variant={MDSTextVariant.TextSm}>{title}</MDSText>
      {stats && (
        <StatsRow>
          <StatBadge variant={MDSTextVariant.TextXs} color={MDSTextColor.Secondary}>
            Count: {stats.blobCount}
          </StatBadge>
          <StatBadge variant={MDSTextVariant.TextXs} color={MDSTextColor.Secondary}>
            Duration: {stats.totalDurationLabel}
          </StatBadge>
          <StatBadge variant={MDSTextVariant.TextXs} color={MDSTextColor.Secondary}>
            Size: {stats.totalSizeLabel}
          </StatBadge>
          <StatBadge variant={MDSTextVariant.TextXs} color={MDSTextColor.Secondary}>
            Uploaded: {stats.uploadedCount}/{stats.blobCount}
          </StatBadge>
        </StatsRow>
      )}
    </SectionHeader>
    <ScrollableContent>
      <AudioRecordingBlobList blobs={blobs} emptyMessage={emptyMessage} />
    </ScrollableContent>
    {playback?.playbackUrl && (
      <AudioPlayer
        controls
        ref={playback.audioElementRef}
        src={playback.playbackUrl}
        onEnded={playback.onAudioEnd}
        onError={playback.onAudioError}
      />
    )}
  </>
);

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const StatsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const StatBadge = styled(MDSText)`
  padding: 4px 10px;
  border-radius: 999px;
  background-color: ${({ theme }) => theme.colors.background.badge.default};
`;

const ScrollableContent = styled.div`
  max-height: 160px;
  overflow-y: auto;
  padding-right: 8px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const AudioPlayer = styled.audio`
  width: 100%;
`;
